import {
    getSourceLayoutPointer,
    getStructurePointer,
    getVirtualStructurePointer
} from '../../conversionDal/utils/pointer'
import type {
    ComponentStageData,
    ConversionDal,
    PluginStageHandler,
    VirtualStructureEntryItem,
    ReadOnlyExtensionAPI
} from '../../types'
import {compareBySOrZRules, generateChildLayout, generateContainerLayout, generatePinnedChildLayout} from './common'
import type {StructureEntryItem} from '../../conversionDal/types'
import {isPinned, updateTargetLayout} from '../common'
import type {Pointer} from '@wix/document-services-types'
import {VIRTUAL_TAG} from '../grouping/constants'

const getRealChildrenAfterOrder = (
    conversionDal: ConversionDal,
    virtualStructurePointer: Pointer,
    realChildren: string[] = []
): string[] => {
    const virtualStructure = conversionDal.get(virtualStructurePointer)

    if (virtualStructure) {
        for (const childId of virtualStructure.components) {
            const virtualChildPointer = getVirtualStructurePointer({id: childId})
            const child = conversionDal.get(virtualChildPointer)

            if (child.componentType === VIRTUAL_TAG) {
                getRealChildrenAfterOrder(conversionDal, virtualChildPointer, realChildren)
            } else {
                realChildren.push(childId)
            }
        }
    }

    return realChildren
}

export const createOrderVirtualStructure: (
    readOnlyExtensionAPI: ReadOnlyExtensionAPI
) => PluginStageHandler<ComponentStageData> =
    readOnlyExtensionAPI =>
    ({componentPointer, conversionDal}: ComponentStageData) => {
        conversionDal.modify(componentPointer, value => ({
            ...value,
            components: [...value.components].sort((childIdA, childIdB) =>
                compareBySOrZRules(
                    readOnlyExtensionAPI.isRTL(),
                    (conversionDal.get(getVirtualStructurePointer({id: childIdA})) as VirtualStructureEntryItem)
                        .measurements,
                    (conversionDal.get(getVirtualStructurePointer({id: childIdB})) as VirtualStructureEntryItem)
                        .measurements
                )
            )
        }))

        return {appliedHeuristics: ['Z ORDER']}
    }

export const orderRealStructure = ({componentPointer, conversionDal}: ComponentStageData) => {
    if (!conversionDal.get(componentPointer)?.components) {
        return
    }

    const virtualStructurePointer = getVirtualStructurePointer(componentPointer)
    const virtualStructure = conversionDal.get(virtualStructurePointer)

    const children: string[] = virtualStructure
        ? getRealChildrenAfterOrder(conversionDal, virtualStructurePointer)
        : conversionDal.get(componentPointer).components

    for (const [index, childId] of children.entries()) {
        const childPointer = getStructurePointer({id: childId})
        if (isPinned({conversionDal, componentPointer: childPointer}))
            updateTargetLayout({conversionDal, componentPointer: childPointer}, generatePinnedChildLayout())
        else updateTargetLayout({conversionDal, componentPointer: childPointer}, generateChildLayout(index + 1))
    }

    conversionDal.modify(componentPointer, (value: StructureEntryItem) => ({
        ...value,
        components: children
    }))
    const {containerLayout} = conversionDal.get(getSourceLayoutPointer(componentPointer)) ?? {}
    updateTargetLayout(
        {conversionDal, componentPointer},
        {
            containerLayout: {
                ...containerLayout,
                ...generateContainerLayout(children.length)
            }
        }
    )
}
